import React, { useCallback, useMemo, useState } from 'react';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { ScopedLink } from '../ScopedLink';
import {
  useListStaffComplianceRegisterQuery,
  useGetStaffComplianceQuery,
} from '../../store/api/localApi';
import { StaffComplianceRegisterEntry } from '../../types';
import { dublinDateString, withinDays } from '../../utils/date';

/**
 * Dashboard tile: ONE number, items expiring in the next 60 days. See
 * task-18-brief CONTROLLER ADDENDUM item 5 — nothing about missing or
 * not-recorded items, no red, no alarm styling. This module is deliberately
 * non-nagging and this tile sits on the product's most-visited screen, so it
 * stays as neutral as the register and profile pages.
 *
 * `useListStaffComplianceRegisterQuery` alone cannot answer "expiring" — the
 * register row (see list-register handler / StaffComplianceRegisterEntry)
 * only carries a derived `vettingStatus` enum and record COUNTS, no dates at
 * all (no revetDueAt, no credential expiryDate). So this follows the exact
 * pattern StaffComplianceRegister.tsx already established for its own
 * "Expiring within 90 days" filter: fetch the register for the roster, then
 * layer in each person's full profile (which does carry dates) and count
 * client-side. One profile fetch per person is fine — location staff lists
 * are small, and RTK Query caches/shares these fetches with the register page.
 */

const EXPIRING_WINDOW_DAYS = 60;

/** Counts one person's items expiring within the window; reports up via callback once resolved. */
const ExpiringCounter: React.FC<{
  entry: StaffComplianceRegisterEntry;
  today: string;
  onCount: (userId: string, count: number) => void;
}> = ({ entry, today, onCount }) => {
  const { data: profile } = useGetStaffComplianceQuery(entry.userId);

  const count = useMemo(() => {
    if (!profile) return 0;
    const expiringVetting = profile.vetting.filter((v) =>
      withinDays(v.revetDueAt, today, EXPIRING_WINDOW_DAYS)
    ).length;
    const expiringCredentials = profile.credentials.filter((c) =>
      withinDays(c.expiryDate, today, EXPIRING_WINDOW_DAYS)
    ).length;
    return expiringVetting + expiringCredentials;
  }, [profile, today]);

  React.useEffect(() => {
    if (profile) onCount(entry.userId, count);
  }, [entry.userId, profile, count, onCount]);

  return null;
};

const StaffComplianceExpiringTile: React.FC<{ locationId: string }> = ({ locationId }) => {
  const { data: registerData, isLoading } = useListStaffComplianceRegisterQuery(locationId);
  const [countsByUserId, setCountsByUserId] = useState<Record<string, number>>({});

  const handleCount = useCallback((userId: string, count: number) => {
    setCountsByUserId((prev) => (prev[userId] === count ? prev : { ...prev, [userId]: count }));
  }, []);

  const register = registerData?.register ?? [];
  const today = dublinDateString(new Date());

  const totalExpiring = useMemo(
    () => Object.values(countsByUserId).reduce((sum, n) => sum + n, 0),
    [countsByUserId]
  );

  if (isLoading || register.length === 0) return null;

  return (
    <ScopedLink
      to="/staff-compliance"
      data-testid="dashboard-expiring-tile"
      className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow block"
    >
      {register.map((entry) => (
        <ExpiringCounter key={entry.userId} entry={entry} today={today} onCount={handleCount} />
      ))}
      <div>
        <div className="absolute rounded-md p-3 bg-slate-500">
          <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
        </div>
        <p className="ml-16 text-sm font-medium text-gray-500 truncate">
          Compliance items expiring soon
        </p>
      </div>
      <div className="ml-16 flex items-baseline pb-6 sm:pb-7">
        <p className="text-2xl font-semibold text-gray-900">{totalExpiring}</p>
      </div>
    </ScopedLink>
  );
};

export default StaffComplianceExpiringTile;
